import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatINR(value) {
  const v = Number(value || 0);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
}

const CHART_MARGIN = { top: 8, right: 16, left: -10, bottom: 0 };
const ACTIVE_DOT = { r: 5 };

function TooltipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const revenue = payload.find((p) => p.dataKey === "revenue")?.value || 0;
  const count = payload[0]?.payload?.count || 0;
  return (
    <div className="bg-white border border-slate-200 rounded-md px-3 py-2 shadow-sm">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900">
        ₹{Number(revenue).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
      </div>
      <div className="text-xs text-slate-500">{count} order{count === 1 ? "" : "s"}</div>
    </div>
  );
}

export default function RevenueTrendChart({ trend }) {
  const data = useMemo(() => {
    return (trend || []).map((b) => {
      const [y, m] = b.key.split("-");
      const monthLabel = `${MONTH_LABELS[parseInt(m, 10) - 1]} ${String(y).slice(2)}`;
      return {
        label: monthLabel,
        revenue: Number(b.revenue || 0),
        count: Number(b.count || 0),
      };
    });
  }, [trend]);

  const totalLast12 = data.reduce((sum, d) => sum + d.revenue, 0);
  const hasData = totalLast12 > 0;

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-6"
      data-testid="revenue-trend-chart"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">
            Revenue trend
          </h3>
          <div className="text-xs text-slate-500 mt-0.5">
            Last 12 months • Total{" "}
            <span className="font-semibold text-slate-700">
              ₹{totalLast12.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
            </span>
          </div>
        </div>
      </div>
      {hasData ? (
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={CHART_MARGIN}
            >
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} tickLine={false} />
              <YAxis
                stroke="#94a3b8"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatINR}
                width={60}
              />
              <Tooltip content={<TooltipBox />} />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#2563eb"
                strokeWidth={2}
                fill="url(#revGrad)"
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[260px] flex items-center justify-center text-sm text-slate-400">
          No revenue recorded in the last 12 months
        </div>
      )}
    </div>
  );
}
